<template>
    <nav class="bg-grey-100 border-b shadow-lg fixed z-50 w-full">
        <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
            <div class="relative flex items-center justify-between h-16">
                <div class="absolute inset-y-0 left-0 flex items-center sm:hidden">
                    <button
                        class="inline-flex items-center justify-center p-2 rounded-md text-glm-black hover:text-white hover:bg-glm-black focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        @click="mobileMenuOpen = !mobileMenuOpen"
                    >
                        <span class="sr-only">
                            Open main menu
                        </span>

                        <svg
                            class="h-6 w-6"
                            :class="[mobileMenuOpen ? 'hidden' : 'block']"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>

                        <svg
                            class="h-6 w-6"
                            :class="[mobileMenuOpen ? 'block' : 'hidden']"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div class="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start">
                    <a class="flex-shrink-0 flex items-center cursor-pointer" href="/" target="_blank">
                        <img
                            class="block lg:hidden h-8 w-auto"
                            src="/data/glmlic_2_icon.svg"
                            alt="Workflow"
                        >

                        <img
                            class="hidden lg:block h-8 w-auto"
                            src="/data/glmlic_2.svg"
                            alt="Workflow"
                        >
                    </a>

                    <div class="hidden sm:block sm:ml-6">
                        <div class="flex space-x-4">
                            <nav-bar-link
                                v-for="(navItem, idx) in navItemsLeft"
                                :key="'left-' + idx"
                                :href="route(navItem.targetRouteName)"
                                :active="isActive(navItem)"
                            >
                                {{ navItem.label }}
                            </nav-bar-link>

                            <a
                                :href="navItem.targetHash"
                                v-for="(navItem, idx) in navItemsMiddle"
                                :key="'middle-' + idx"
                                @click="onNavItemClick"
                                :class="getNavItemClasses(isThisHashActive(navItem.targetHash))"
                            >
                                {{ navItem.label }}
                            </a>

                            <nav-bar-link
                                v-for="(navItem, idx) in navItemsRight"
                                :key="'right-' + idx"
                                :href="route(navItem.targetRouteName)"
                                :active="isActive(navItem)"
                                class="block sm:hidden lg:block"
                            >
                                {{ navItem.label }}
                            </nav-bar-link>

                            <button
                                id="dropdownMoreButton"
                                data-dropdown-toggle="dropdown-more"
                                class="hidden sm:block lg:hidden"
                                :class="getNavItemClasses(false)"
                                type="button"
                            >
                                More
                            </button>

                            <div
                                id="dropdown-more"
                                class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-44"
                            >
                                <ul class="py-2 text-sm text-gray-700"
                                    aria-labelledby="dropdownMoreButton"
                                >
                                    <li
                                        v-for="(navItem, idx) in navItemsRight"
                                        :key="'right-' + idx"
                                    >
                                        <a
                                            :href="route(navItem.targetRouteName)"
                                            class="block px-4 py-2 hover:bg-gray-100"
                                        >
                                            {{ navItem.label }}
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <button
                                id="researchDropdownButton"
                                :class="getNavItemClasses(isResearchActive(null))"
                                type="button"
                            >
                                Research
                            </button>

                            <div id="researchDropdown"
                                class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-44"
                            >
                                <ul class="py-2 text-sm text-gray-700">
                                    <li>
                                        <inertia-link
                                            :href="route('publications.show', {'type': 'policy_brief'})"
                                            class="block px-4 py-2 hover:bg-gray-100"
                                            :class="{'text-red-500' : isResearchActive('policy_brief') }"
                                        >
                                            Policy Briefs
                                        </inertia-link>
                                    </li>

                                    <li>
                                        <inertia-link
                                            :href="route('publications.show', {'type': 'synthesis_paper'})"
                                            class="block px-4 py-2 hover:bg-gray-100"
                                            :class="{'text-red-500' : isResearchActive('synthesis_paper') }"
                                        >
                                            Synthesis Papers
                                        </inertia-link>
                                    </li>

                                    <li>
                                        <inertia-link
                                            :href="route('publications.show', {'type': 'working_paper'})"
                                            class="block px-4 py-2 hover:bg-gray-100"
                                            :class="{'text-red-500' : isResearchActive('working_paper') }"
                                        >
                                            Working Papers
                                        </inertia-link>
                                    </li>

                                    <li>
                                        <inertia-link
                                            :href="route('publications.show', {'type': 'published_articles'})"
                                            class="block px-4 py-2 hover:bg-gray-100"
                                            :class="{'text-red-500' : isResearchActive('published_articles') }"
                                        >
                                            Published Articles
                                        </inertia-link>
                                    </li>

                                    <li>
                                        <inertia-link
                                            :href="route('publications.show', {'type': 'further_papers'})"
                                            class="block px-4 py-2 hover:bg-gray-100"
                                            :class="{'text-red-500' : isResearchActive('further_papers') }"
                                        >
                                            Further Papers
                                        </inertia-link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="sm:hidden" :class="[mobileMenuOpen ? 'block' : 'hidden']">
            <div class="px-2 pt-2 pb-3 space-y-1">
                <nav-bar-link
                    v-for="(navItem, idx) in navItemsLeft"
                    :key="'left-m-' + idx"
                    :href="route(navItem.targetRouteName)"
                    :active="isActive(navItem)"
                    class="block"
                >
                    {{ navItem.label }}
                </nav-bar-link>

                <a
                    :href="navItem.targetHash"
                    v-for="(navItem, idx) in navItemsMiddle"
                    :key="'middle-m-' + idx"
                    @click="onNavItemClick"
                    :class="getNavItemClasses(isThisHashActive(navItem.targetHash))"
                    class="block"
                >
                    {{ navItem.label }}
                </a>

                <nav-bar-link
                    v-for="(navItem, idx) in navItemsRight"
                    :key="'right-m-' + idx"
                    :href="route(navItem.targetRouteName)"
                    :active="isActive(navItem)"
                    class="block"
                >
                    {{ navItem.label }}
                </nav-bar-link>

                <a
                    href="#"
                    class="block"
                    :class="getNavItemClasses(false)"
                >
                    Research
                </a>
            </div>
        </div>
    </nav>
</template>

<script>
import NavBarLink from '@/Components/NavBarLink'
import { Dropdown } from 'flowbite'

export default {
    components: {
        NavBarLink,
    },

    data () {
        return {
            key: 0,
            mobileMenuOpen: false,
            currentHash: window.location.hash,
            currentElement: null,

            navItemsLeft: [
                {
                    label: 'Home',
                    targetRouteName: 'welcome',
                },
            ],

            navItemsMiddle: [
                // {
                //     label     : 'news',
                //     targetHash: '#news',
                // },
                // {
                //     label: 'About',
                //     targetHash: '#about',
                // },
                {
                    label: 'Data Download',
                    targetHash: '#download',
                },
                // {
                //     label: 'Contact',
                //     targetHash: '#contact',
                // },
            ],

            navItemsRight: [
                {
                    label: 'Data Visualization',
                    targetRouteName: 'charts',
                },
                {
                    label: 'Variable Description',
                    targetRouteName: 'variable.definition.show',
                },
                // {
                //    label          : 'Videos',
                //    targetRouteName: 'videos',
                // },
                // {
                //    label          : 'Research',
                //    targetRouteName: 'research',
                // },
                {
                    label: 'Contact Our Team',
                    targetRouteName: 'team',
                },
                {
                    label: 'Acknowledgements',
                    targetRouteName: 'acknowledgements',
                },
            ],

            researchDropdown: null,
        }
    },

    mounted () {
        const $targetEl = document.getElementById('researchDropdown')
        const $triggerEl = document.getElementById('researchDropdownButton')

        // options with default values
        const options = {
            placement: 'bottom',
            triggerType: 'click',
            offsetSkidding: 0,
            offsetDistance: 10,
            delay: 300,
            ignoreClickOutsideClass: false,
            onHide: () => {
                //
            },
            onShow: () => {
                //
            },
            onToggle: () => {
                //
            },
        }

        const instanceOptions = {
            id: 'researchDropdown',
            override: true
        }

        this.researchDropdown = new Dropdown($targetEl, $triggerEl, options, instanceOptions)
    },

    created () {
        window.addEventListener('scroll', this.onScroll)
    },

    destroyed () {
        window.removeEventListener('scroll', this.onScroll)
    },

    methods: {
        onLogoClick () {
            this.$inertia.visit(route('welcome'))
        },

        onNavItemClick (e) {
            let href = e.target.getAttribute('href'),
                args = {
                    scrollTo: document.getElementById(href.replace('#', '')),
                    hash: href,
                    offset: -64,
                },
                _this = this

            e.preventDefault()
            this.mobileMenuOpen = false
            this.currentHash = href

            if (this.$page.props.currentRouteName !== 'welcome') {
                this.$inertia.visit(route('welcome') + '/' + href, {
                    onSuccess: () => {
                        args.scrollTo = document.getElementById(href.replace('#', ''))

                        window.removeEventListener('scroll', _this.onScroll)
                        _this.$smoothScroll(args)

                        setTimeout(function () {
                            window.addEventListener('scroll', _this.onScroll)
                        }, 501)
                    },
                })

                return false
            }

            window.removeEventListener('scroll', this.onScroll)
            this.$smoothScroll(args)

            setTimeout(function () {
                window.addEventListener('scroll', _this.onScroll)
            }, 501)

            return false
        },

        getNavItemClasses (active) {
            return active
                ? 'bg-glm-red text-white block px-3 py-2 rounded-md text-base font-medium'
                : 'transition-all text-glm-black hover:bg-grey-600 hover:text-white block px-3 py-2 rounded-md text-base font-medium'
        },

        isThisHashActive (hash) {
            return this.currentHash === hash
        },

        isHashActive () {
            return this.currentHash !== null && this.currentHash !== ''
        },

        isActive (navItem) {
            if (this.$page.props.currentRouteName === 'welcome' && this.$page.props.currentRouteName === navItem.targetRouteName) {
                return !this.isHashActive()
            }

            return this.$page.props.currentRouteName === navItem.targetRouteName
        },

        isResearchActive (researchType) {
            if (researchType === null) {
                return this.$page.props.currentRouteName === 'publications.show'
            }

            return this.$page.props.selectedType === researchType && this.$page.props.currentRouteName === 'publications.show'
        },

        hasId (element) {
            return typeof element.id != 'undefined'
        },

        onScroll () {
            let elem = document.elementFromPoint(0, 65)

            if (this.currentElement === null || this.currentElement !== elem) {
                let parent = null

                // find next section
                for (; elem && elem !== document; elem = elem.parentNode) {
                    if ('.section' && elem.matches('.section')) {
                        parent = elem
                    }
                }

                if (parent === null) {
                    this.currentElement = null
                    this.currentHash = null

                    return
                }

                if (this.currentElement === parent) return

                this.currentElement = parent

                if (!this.hasId(parent)) return

                let foundNavItem = _.find(this.navItemsMiddle, ['targetHash', '#' + this.currentElement.id])

                if (!foundNavItem) {
                    this.currentHash = null

                    return
                }

                this.currentHash = '#' + this.currentElement.id
            }
        },
    },
}
</script>
